import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, UserPlus, Pencil, Trash2, Users } from "lucide-react";

const WRITE_ROLES = ["admin", "manager"];
const ROLE_OPTIONS = [
  { value: "sales", label: "Sales" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
];
const ROLE_COLORS = {
  admin: "bg-[#0055FF]/10 text-[#0055FF]",
  manager: "bg-[#10B981]/10 text-[#10B981]",
  dispatcher: "bg-[#F59E0B]/10 text-[#F59E0B]",
  sales: "bg-[#71717A]/10 text-[#71717A]",
};

export default function TeamPage() {
  const { role, user, activeTenantId } = useAuth();
  const canWrite = WRITE_ROLES.includes(role);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "sales", territories: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get("/memberships"),
        api.get("/territories").catch(() => ({ data: [] })),
      ]);
      setMembers(m.data);
      setTerritories(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sin permisos para ver el equipo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId && canWrite) load(); else setLoading(false); }, [activeTenantId, canWrite, load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ email: "", name: "", role: "sales", territories: [] });
    setOpen(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ email: m.email, name: m.name, role: m.role, territories: m.territories || [] });
    setOpen(true);
  };

  const toggleTerritory = (id) =>
    setForm((f) => ({
      ...f,
      territories: f.territories.includes(id) ? f.territories.filter((x) => x !== id) : [...f.territories, id],
    }));

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/memberships/${editing.id}`, { role: form.role, territories: form.territories });
      } else {
        await api.post("/memberships", { email: form.email, name: form.name, role: form.role, territories: form.territories });
      }
      toast.success(editing ? "Miembro actualizado" : "Miembro agregado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/memberships/${m.id}`);
      toast.success("Miembro removido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo remover");
    }
  };

  if (!canWrite) {
    return (
      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-10 text-center" data-testid="team-no-access">
        <Users className="h-8 w-8 text-[#A1A1AA] mx-auto" />
        <p className="mt-3 text-sm text-[#71717A]">Tu rol no tiene acceso a la gestión del equipo.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A]">Miembros del tenant, sus roles y territorios asignados.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-member-button" onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md active:scale-[0.98] transition-colors">
              <UserPlus className="h-4 w-4 mr-1" /> Agregar miembro
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="member-dialog">
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar miembro" : "Agregar miembro"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              {!editing && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#71717A]">Email</Label>
                    <Input data-testid="member-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#71717A]">Nombre</Label>
                    <Input data-testid="member-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.role === "sales" && (
                <div className="space-y-2">
                  <Label className="text-xs text-[#71717A]">Territorios asignados</Label>
                  {territories.length === 0 && <p className="text-xs text-[#A1A1AA]">No hay territorios creados.</p>}
                  <div className="space-y-2 max-h-40 overflow-auto">
                    {territories.map((t) => (
                      <label key={t.id} className="flex items-center gap-2 text-sm" data-testid={`territory-check-${t.id}`}>
                        <Checkbox checked={form.territories.includes(t.id)} onCheckedChange={() => toggleTerritory(t.id)} />
                        {t.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="save-member-button" onClick={save} disabled={!editing && !form.email} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
        ) : (
          <Table data-testid="team-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Territorios</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                  <TableCell className="font-medium">{m.name}{m.user_id === user?.user_id && <span className="text-[#71717A] text-xs"> (vos)</span>}</TableCell>
                  <TableCell className="text-[#71717A]">{m.email}</TableCell>
                  <TableCell>
                    <Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${ROLE_COLORS[m.role]}`}>{m.role}</Badge>
                  </TableCell>
                  <TableCell className="text-[#71717A]">{(m.territories || []).length || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                    {m.user_id !== user?.user_id && (
                      <Button variant="ghost" size="icon" onClick={() => remove(m)} data-testid={`delete-member-${m.id}`} className="text-[#EF4444]"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
